import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { backendEndPointToken } from '../../app.config';
import type { ImagingStudy, ServiceRequest } from 'fhir/r4';
import { StateService } from '../../shared/state.service';
import { ErrorService } from '../../shared/error.service';
import { ImagingStudyImagesComponent } from '../../shared/imaging-study-images/imaging-study-images.component';
import type { ImagingStudyLocalImage } from '../../shared/imaging-study-images/imaging-study-images.util';
import { imagingStudyExtensionsToImages, upsertImagingStudyImages } from '../../shared/imaging-study-images/imaging-study-images.util';

export interface AddImagingStudyDialogData {
    serviceRequest: ServiceRequest;
    patientId: string;
    existingImagingStudy?: ImagingStudy | null;
}

@Component({
    selector: 'app-add-imaging-study',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        ImagingStudyImagesComponent
    ],
    templateUrl: './add-imaging-study.component.html',
    styleUrl: './add-imaging-study.component.scss'
})
export class AddImagingStudyComponent {
    private readonly http = inject(HttpClient);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly stateService = inject(StateService);
    private readonly errorService = inject(ErrorService);

    private readonly fb = inject(FormBuilder);
    private readonly dialogRef = inject(MatDialogRef<AddImagingStudyComponent, ImagingStudy | null>);
    readonly data = inject<AddImagingStudyDialogData>(MAT_DIALOG_DATA);

    readonly statusOptions: Array<ImagingStudy['status']> = ['registered', 'available', 'cancelled', 'entered-in-error', 'unknown'];

    images: ImagingStudyLocalImage[] = [];

    private readonly azureBlobNameExtensionUrl = 'https://elikita-server.daalitech.com/fhir/StructureDefinition/azureBlobName';

    private normalizeUploadUiFileToImagingLocalImage(file: any): ImagingStudyLocalImage | null {
        if (!file) return null;
        const title = (file.uploadFileName ?? file.title ?? file.name ?? '').toString().trim();
        const contentType = (file.uploadFileType ?? file.contentType ?? file.type ?? '').toString().trim() || 'application/octet-stream';
        const sizeRaw = file.uploadFileSize ?? file.size;
        const size = Number.isFinite(Number(sizeRaw)) ? Number(sizeRaw) : 0;
        const creation = (file.uploadDate ?? file.creation ?? new Date().toISOString()).toString().trim();
        const fileLink = (file.fileLink ?? file.url ?? '').toString().trim();
        const azureBlobName = (file.azureBlobName ?? '').toString().trim();

        const id = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        return {
            id,
            fileName: title || 'image',
            contentType,
            size,
            createdAt: creation,
            dataUrl: fileLink || '',
            base64: '',
            selected: false,
            ...(fileLink ? { fileLink } : {}),
            ...(azureBlobName ? { azureBlobName } : {})
        } as any;
    }

    onImagingCloudFiles(files: any[]): void {
        if (!Array.isArray(files) || !files.length) return;
        const mapped = files.map((f) => this.normalizeUploadUiFileToImagingLocalImage(f)).filter(Boolean) as ImagingStudyLocalImage[];
        if (!mapped.length) return;
        // append to images list and emit
        this.images = [...(this.images || []), ...mapped];
    }

    readonly form = this.fb.group({
        status: this.fb.control<ImagingStudy['status']>('registered', { nonNullable: true, validators: [Validators.required] }),
        description: this.fb.control<string>('', { nonNullable: true }),
        started: this.fb.control<string>('', { nonNullable: true }) // datetime-local string (or empty)
    });

    ngOnInit(): void {
        const sr = this.data?.serviceRequest;
        const patientId = (this.data?.patientId || '').toString().trim();
        if (!sr?.id || !patientId) {
            this.errorService.openandCloseError('Missing context for ImagingStudy form.');
            this.dialogRef.close(null);
            return;
        }

        const existing = this.data?.existingImagingStudy || null;
        if (existing) {
            this.form.patchValue({
                status: (existing.status as any) || 'registered',
                description: (existing as any).description || '',
                started: this.toDateTimeLocal(existing.started)
            });

            const ext = Array.isArray((existing as any)?.extension) ? ((existing as any).extension as any[]) : [];
            this.images = imagingStudyExtensionsToImages(ext);
        } else {
            // Default the description from the ServiceRequest code/display
            const display =
                (sr as any)?.code?.text ||
                (sr as any)?.code?.coding?.[0]?.display ||
                (sr as any)?.code?.coding?.[0]?.code ||
                'Imaging';

            this.form.patchValue({
                description: display,
                status: 'registered'
            });
        }
    }

    close(): void {
        this.dialogRef.close(null);
    }

    async save(): Promise<void> {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.errorService.openandCloseError('Please fix the form errors.');
            return;
        }

        const sr = this.data.serviceRequest;
        const patientId = this.data.patientId;
        const srRef = sr?.id ? `ServiceRequest/${sr.id}` : '';
        if (!srRef) {
            this.errorService.openandCloseError('ServiceRequest ID missing.');
            return;
        }

        const existing = this.data.existingImagingStudy || null;

        const next: ImagingStudy = {
            ...(existing as any),
            resourceType: 'ImagingStudy',
            status: this.form.controls.status.value as any,
            description: (this.form.controls.description.value || '').toString().trim() || undefined,
            started: this.fromDateTimeLocal(this.form.controls.started.value) || undefined,
            subject: { reference: `Patient/${patientId}` } as any,
            basedOn: this.ensureBasedOn(existing?.basedOn, srRef)
        } as any;

        const withImages = this.images?.length ? upsertImagingStudyImages(next, this.images) : next;

        try {
            const saved = existing?.id
                ? await this.putImagingStudy(existing.id, withImages)
                : await this.postImagingStudy(withImages);

            this.stateService.persistLocalResource(saved as any, 'saved');
            this.dialogRef.close(saved);
        } catch (e) {
            console.error(e);
            this.errorService.openandCloseError('Failed to save ImagingStudy.');
        }
    }

    private ensureBasedOn(
        existing: Array<{ reference?: string }> | undefined,
        srRef: string
    ): Array<{ reference: string }> {
        const list = Array.isArray(existing) ? existing : [];
        const kept = list
            .map((x) => (x?.reference || '').toString().trim())
            .filter((ref) => !!ref && ref !== srRef)
            .map((ref) => ({ reference: ref }));

        return [...kept, { reference: srRef }];
    }

    private fhirHeaders(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/fhir+json',
            Accept: 'application/fhir+json',
            Prefer: 'return=representation'
        });
    }

    private putImagingStudy(id: string, body: ImagingStudy): Promise<ImagingStudy> {
        return this.http
            .put<ImagingStudy>(`${this.backendUrl}/ImagingStudy/${encodeURIComponent(id)}`, body as any, { headers: this.fhirHeaders() })
            .toPromise() as Promise<ImagingStudy>;
    }

    private postImagingStudy(body: ImagingStudy): Promise<ImagingStudy> {
        return this.http
            .post<ImagingStudy>(`${this.backendUrl}/ImagingStudy`, body as any, { headers: this.fhirHeaders() })
            .toPromise() as Promise<ImagingStudy>;
    }

    private toDateTimeLocal(iso: string | undefined): string {
        if (!iso) return '';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '';
        const pad = (n: number) => String(n).padStart(2, '0');
        // local datetime-local format: YYYY-MM-DDTHH:mm
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    private fromDateTimeLocal(value: string | null | undefined): string {
        const v = (value || '').toString().trim();
        if (!v) return '';
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return '';
        return d.toISOString();
    }
}
